import { wrapCurrency } from '@/utils/tool';
import classNames from 'classnames';
import { ProductCardProps } from '../../types';
import { twMerge } from 'tailwind-merge';
import VolumePricing from '../../volumePricing';

const getBasePrice = (data, showBasePrice, showSpecialPrice) => {
  if (!!showBasePrice && !!data?.base_price) {
    let comparePrice = data?.price;
    //秒杀价取值调整
    if (showSpecialPrice) {
      comparePrice = data?.special_price;
    }
    if (data?.base_price > comparePrice) {
      return data?.base_price;
    }
  }
  return null;
};

const CardBodyPrice: React.FC<Partial<
  ProductCardProps & {
    showSpecialPrice?: boolean;
    volumeCls?: string;
  }
>> = props => {
  const {
    data,
    showBasePrice = true,
    showSpecialPrice,
    className,
    basePriceCls,
    priceCls,
    volumeCls,
    volumeClasses
  } = props;

  const priceId = `${data.id || data.product_id}-price`;

  const renderBasePrice = () => {
    if (!!showBasePrice && !!data?.base_price) {
      let comparePrice = data?.price;
      //秒杀价取值调整
      if (showSpecialPrice) {
        comparePrice = data?.special_price;
      }
      if (data?.base_price > comparePrice) {
        return (
          <div
            data-testid="wid-product-card-base-price"
            className={twMerge(classNames('enki-body-sm text-surface-100-fg-minor line-through'), basePriceCls)}>
            {`$${wrapCurrency(data?.base_price)}`}
          </div>
        );
      }
    }
    return null;
  };

  const renderSubscribePrice = () => {
    let comparePrice = data?.price;
    if (showSpecialPrice) {
      comparePrice = data?.special_price;
    }

    // 1. Whether to display the subscription price: must be present and strictly cheaper than the current selling price.
    const hasValidSubscribePrice = !!data?.subscribe_price && !!comparePrice && data.subscribe_price < comparePrice;

    // 2. Whether to display the subscription tip: text must exist and discount percentage must not be 0.
    const hasValidSubscribeTip =
      !!data?.subscribe_price_tip &&
      !(
        data?.subscribe_discount_percentage !== undefined &&
        data?.subscribe_discount_percentage !== null &&
        Number(data.subscribe_discount_percentage) === 0
      );

    if (!hasValidSubscribePrice && !hasValidSubscribeTip) {
      return null;
    }

    return (
      <div
        data-testid="wid-product-card-subscribe-price"
        className="flex items-center mt-0.5 text-shade-cool-base-7 line-clamp-1">
        {hasValidSubscribePrice && (
          <span data-testid="txt-product-card-subscribe-price" className="enki-body-2xs-medium mr-1">{`$${wrapCurrency(
            data?.subscribe_price
          )}`}</span>
        )}
        {hasValidSubscribeTip && (
          <span
            data-testid="txt-product-card-subscribe-price-tip"
            className="enki-body-3xs mt-[1px]"
            dangerouslySetInnerHTML={{ __html: `(${data?.subscribe_price_tip})` }}></span>
        )}
      </div>
    );
  };

  return (
    <div data-testid="wid-product-card-price" role="group" className={twMerge('mb-1', className)}>
      {!!data?.volume_price_support ? (
        <VolumePricing
          className={volumeCls}
          classes={volumeClasses}
          volumePrice={String(data?.volume_price)}
          price={showSpecialPrice ? data?.special_price : data?.price}
          basePrice={getBasePrice(data, showBasePrice, showSpecialPrice)}
          volumeThreshold={data?.volume_threshold}
        />
      ) : (
        <div className="flex items-baseline flex-row flex-wrap">
          <div
            className={twMerge(
              'enki-body-base-strong text-pricing-txt mr-1',
              props.cardSize === 'sm' && 'leading-5 text-[17px]',
              priceCls
            )}
            id={priceId}
            data-testid="wid-product-card-price-value"
            data-role="text">
            {`$${wrapCurrency(showSpecialPrice ? data?.special_price : data?.price)}`}
          </div>

          {renderBasePrice()}
        </div>
      )}
      {/* Render subscription price row */}
      {renderSubscribePrice()}
    </div>
  );
};

export default CardBodyPrice;
